import { APIFailureAction, APIRequestAction, APISuccessAction } from 'mk2/helpers/action';
import { NormalizedResponse } from 'mk2/helpers/api';
import { Action } from 'redux';

///////////////////////////////////////////////////////////////////
// Concepts load

export const CONCEPTS_FETCH_TRIGGER = 'CONCEPTS_FETCH_TRIGGER';
export interface ConceptsFetchTriggerAction {
    readonly type: typeof CONCEPTS_FETCH_TRIGGER;
    readonly id: number;
    readonly key: string;
    readonly entityType: number;
}
interface ItemResultsRequest {
    [key: string]: {
        id: number;
    };
}
export const CONCEPTS_FETCH_REQUEST = 'CONCEPTS_FETCH_REQUEST';
export interface ConceptsFetchRequestAction extends APIRequestAction {
    readonly type: typeof CONCEPTS_FETCH_REQUEST;
    readonly items: ItemResultsRequest;
    readonly entityType: number;
}
export interface ConceptsFetchNormalizedResponse extends NormalizedResponse {
    readonly result: { concepts: number[], articleConceptMapping: {[id: number]: string}, selectSuggestions: any };
}
interface ItemResultsSuccess {
    [key: string]: {
        id: number;
        conceptIds: number[];
        articleConceptMapping: {[id: number]: string};
    };
}
export const CONCEPTS_FETCH_SUCCESS = 'CONCEPTS_FETCH_SUCCESS';
export interface ConceptsFetchSuccessAction extends APISuccessAction<ConceptsFetchNormalizedResponse> {
    readonly type: typeof CONCEPTS_FETCH_SUCCESS;
    readonly items: ItemResultsSuccess;
    readonly response: ConceptsFetchNormalizedResponse;
    readonly entityType: number;
}
interface ItemResultsFailure {
    [key: string]: {
        id: number;
    };
}
export const CONCEPTS_FETCH_FAILURE = 'CONCEPTS_FETCH_FAILURE';
export interface ConceptsFetchFailureAction extends APIFailureAction {
    readonly type: typeof CONCEPTS_FETCH_FAILURE;
    readonly items: ItemResultsFailure;
    readonly entityType: number;
}

export const conceptsFetchTrigger =
    (id: number, key: string, entityType: number): ConceptsFetchTriggerAction => ({
        type: CONCEPTS_FETCH_TRIGGER, id, key, entityType,
    });
export const conceptsFetchApi = {
    request: (items: ItemResultsRequest, entityType: number): ConceptsFetchRequestAction => ({
        type: CONCEPTS_FETCH_REQUEST, items, entityType,
    }),
    success: (items: ItemResultsSuccess, response: ConceptsFetchNormalizedResponse, entityType: number): ConceptsFetchSuccessAction => ({
        type: CONCEPTS_FETCH_SUCCESS, items, response, entityType,
    }),
    failure: (items: ItemResultsFailure, error: any, entityType: number): ConceptsFetchFailureAction => ({
        type: CONCEPTS_FETCH_FAILURE, items, error, entityType,
    }),
};

///////////////////////////////////////////////////////////////////
// Add concepts

export interface AddConceptAction extends Action {
    readonly id: number;
    readonly conceptId: number;
    readonly key: string;
    readonly entityType: number;
}

export const ADD_CONCEPT_TRIGGER = 'ADD_CONCEPT_TRIGGER';
export interface AddConceptTriggerAction extends AddConceptAction, Action {
    readonly type: typeof ADD_CONCEPT_TRIGGER;
}
export const ADD_CONCEPT_REQUEST = 'ADD_CONCEPT_REQUEST';
export interface AddConceptRequestAction extends AddConceptAction, APIRequestAction {
    readonly type: typeof ADD_CONCEPT_REQUEST;
}
export interface AddConceptNormalizedResponse extends NormalizedResponse {
    readonly result: { concept: number, articleConceptMapping: {[id: number]: string} };
}
export const ADD_CONCEPT_SUCCESS = 'ADD_CONCEPT_SUCCESS';
export interface AddConceptSuccessAction extends AddConceptAction, APISuccessAction<AddConceptNormalizedResponse> {
    readonly type: typeof ADD_CONCEPT_SUCCESS;
}
export const ADD_CONCEPT_FAILURE = 'ADD_CONCEPT_FAILURE';
export interface AddConceptFailureAction extends AddConceptAction, APIFailureAction {
    readonly type: typeof ADD_CONCEPT_FAILURE;
}

export const addConceptTrigger =
    (id: number, conceptId: number, key: string, entityType: number): AddConceptTriggerAction => ({
        type: ADD_CONCEPT_TRIGGER, id, conceptId, key, entityType,
    });
export const addConceptActions = {
    request: (id: number, conceptId: number, key: string, entityType: number): AddConceptRequestAction => ({
        type: ADD_CONCEPT_REQUEST, id, conceptId, key, entityType,
    }),
    success: (id: number, conceptId: number, key: string, entityType: number, response: AddConceptNormalizedResponse): AddConceptSuccessAction => ({
        type: ADD_CONCEPT_SUCCESS, id, conceptId, key, entityType, response,
    }),
    failure: (id: number, conceptId: number, key: string, entityType: number, error: any): AddConceptFailureAction => ({
        type: ADD_CONCEPT_FAILURE, id, conceptId, key, entityType, error,
    }),
};

///////////////////////////////////////////////////////////////////
// Remove concept

export interface RemoveConceptAction extends Action {
    readonly id: number;
    readonly conceptId: number;
    readonly key: string;
    readonly entityType: number;
}

export const REMOVE_CONCEPT_TRIGGER = 'REMOVE_CONCEPT_TRIGGER';
export interface RemoveConceptTriggerAction extends RemoveConceptAction, Action {
    readonly type: typeof REMOVE_CONCEPT_TRIGGER;
}
export const REMOVE_CONCEPT_REQUEST = 'REMOVE_CONCEPT_REQUEST';
export interface RemoveConceptRequestAction extends RemoveConceptAction, APIRequestAction {
    readonly type: typeof REMOVE_CONCEPT_REQUEST;
}
export const REMOVE_CONCEPT_SUCCESS = 'REMOVE_CONCEPT_SUCCESS';
export interface RemoveConceptSuccessAction extends RemoveConceptAction {
    readonly type: typeof REMOVE_CONCEPT_SUCCESS;
}
export const REMOVE_CONCEPT_FAILURE = 'REMOVE_CONCEPT_FAILURE';
export interface RemoveConceptFailureAction extends RemoveConceptAction, APIFailureAction {
    readonly type: typeof REMOVE_CONCEPT_FAILURE;
}

export const removeConceptTrigger =
    (id: number, conceptId: number, key: string, entityType: number): RemoveConceptTriggerAction => ({
        type: REMOVE_CONCEPT_TRIGGER, id, conceptId, key, entityType,
    });
export const removeConceptActions = {
    request: (id: number, conceptId: number, key: string, entityType: number): RemoveConceptRequestAction => ({
        type: REMOVE_CONCEPT_REQUEST, id, conceptId, key, entityType,
    }),
    success: (id: number, conceptId: number, key: string, entityType: number): RemoveConceptSuccessAction => ({
        type: REMOVE_CONCEPT_SUCCESS, id, conceptId, key, entityType,
    }),
    failure: (id: number, conceptId: number, key: string, entityType: number, error: any): RemoveConceptFailureAction => ({
        type: REMOVE_CONCEPT_FAILURE, id, conceptId, key, entityType, error,
    }),
};

///////////////////////////////////////////////////////////////////
// Update concept position

export interface UpdateConceptsPositionAction extends Action {
    readonly id: number;
    readonly conceptId: number;
    readonly key: string;
    readonly entityType: number;
}

export const UPDATE_CONCEPT_POSITION_TRIGGER = 'UPDATE_CONCEPT_POSITION_TRIGGER';
export interface UpdateConceptsPositionTriggerAction extends UpdateConceptsPositionAction, Action {
    readonly type: typeof UPDATE_CONCEPT_POSITION_TRIGGER;
}
export const UPDATE_CONCEPT_POSITION_REQUEST = 'UPDATE_CONCEPT_POSITION_REQUEST';
export interface UpdateConceptsPositionRequestAction extends UpdateConceptsPositionAction, APIRequestAction {
    readonly type: typeof UPDATE_CONCEPT_POSITION_REQUEST;
}

export interface UpdateConceptsPositionNormalizedResponse extends NormalizedResponse {
    readonly result: { concepts: number[] };
}

export const UPDATE_CONCEPT_POSITION_SUCCESS = 'UPDATE_CONCEPT_POSITION_SUCCESS';
export interface UpdateConceptsPositionSuccessAction extends UpdateConceptsPositionAction {
    readonly type: typeof UPDATE_CONCEPT_POSITION_SUCCESS;
    readonly response: UpdateConceptsPositionNormalizedResponse;
}
export const UPDATE_CONCEPT_POSITION_FAILURE = 'UPDATE_CONCEPT_POSITION_FAILURE';
export interface UpdateConceptsPositionFailureAction extends UpdateConceptsPositionAction, APIFailureAction {
    readonly type: typeof UPDATE_CONCEPT_POSITION_FAILURE;
}

export const updateConceptPositionTrigger =
    (id: number, conceptId: number, key: string, entityType: number): UpdateConceptsPositionTriggerAction => ({
        type: UPDATE_CONCEPT_POSITION_TRIGGER, id, conceptId, key, entityType,
    });
export const updateConceptPositionActions = {
    request: (id: number, conceptId: number, key: string, entityType: number): UpdateConceptsPositionRequestAction => ({
        type: UPDATE_CONCEPT_POSITION_REQUEST, id, conceptId, key, entityType,
    }),
    success: (id: number, conceptId: number, key: string, entityType: number, response: UpdateConceptsPositionNormalizedResponse): UpdateConceptsPositionSuccessAction => ({
        type: UPDATE_CONCEPT_POSITION_SUCCESS, id, conceptId, key, entityType, response,
    }),
    failure: (id: number, conceptId: number, key: string, entityType: number, error: any): UpdateConceptsPositionFailureAction => ({
        type: UPDATE_CONCEPT_POSITION_FAILURE, id, conceptId, key, entityType, error,
    }),
};
